// Lokaler Beispieldatensatz.
//
// Erzeugt exakt die Struktur, die der Publisher später als JSON ausliefert:
// alle Zeitpunkte als Unix-Sekunden in UTC. Der Tages-Cut liegt auf 00:00 UTC,
// die Zinsentscheidungen auf der Ankündigungsminute der jeweiligen Bank.
//
// Der Unterschied zwischen Ankündigung und Wirksamkeit steckt allein in
// `pendingBp`: die Fed ändert sofort, die EZB sechs Tage später. Damit lässt
// sich das Band im Chart ohne Netz prüfen.
//
// Deterministisch, damit der Chart bei jedem Reload gleich aussieht.

import type { Bank, Meeting, PiprBundle, PiprForward, PiprHistory } from "../types";

const DELTAS = [-50, -25, 0, 25, 50];
const DAY = 86400;
const END: [number, number, number] = [2026, 9, 12];

const LATER = [
    [30, 120, 520, 300, 30],
    [60, 180, 460, 250, 50],
    [90, 220, 420, 220, 50],
];

interface Spec {
    start: [number, number, number];
    anchorStart: number;
    hourUtc: number;
    minuteUtc: number;
    effectiveLagDays: number;
    assumedNext: number;
    seed: number;
    meetings: Array<[[number, number, number], number | null]>;
}

const SPECS: Record<Bank, Spec> = {
    fed: {
        start: [2024, 5, 3],
        anchorStart: 550,
        hourUtc: 18,
        minuteUtc: 0,
        effectiveLagDays: 0,
        assumedNext: -25,
        seed: 11,
        meetings: [
            [[2024, 6, 12], 0],
            [[2024, 7, 31], 0],
            [[2024, 9, 18], -50],
            [[2024, 11, 7], -25],
            [[2024, 12, 18], -25],
            [[2025, 1, 29], 0],
            [[2025, 3, 19], 0],
            [[2025, 5, 7], 0],
            [[2025, 6, 18], 0],
            [[2025, 7, 30], 0],
            [[2025, 9, 17], -25],
            [[2025, 10, 29], -25],
            [[2025, 12, 10], 0],
            [[2026, 1, 28], -25],
            [[2026, 3, 18], 0],
            [[2026, 4, 29], -25],
            [[2026, 6, 17], 0],
            [[2026, 7, 29], -25],
            [[2026, 9, 16], null],
            [[2026, 11, 4], null],
            [[2026, 12, 16], null],
            [[2027, 1, 27], null],
        ],
    },
    ecb: {
        start: [2025, 7, 26],
        anchorStart: 250,
        hourUtc: 12,
        minuteUtc: 15,
        effectiveLagDays: 6,
        assumedNext: 0,
        seed: 29,
        meetings: [
            [[2025, 9, 11], 0],
            [[2025, 10, 30], 0],
            [[2025, 12, 18], -25],
            [[2026, 2, 5], 0],
            [[2026, 3, 12], 0],
            [[2026, 4, 30], 0],
            [[2026, 6, 11], 0],
            [[2026, 7, 23], 0],
            [[2026, 9, 10], 25],
            [[2026, 10, 29], null],
        ],
    },
};

const utcTs = (y: number, m: number, d: number, hh = 0, mm = 0) =>
    Math.floor(Date.UTC(y, m - 1, d, hh, mm, 0) / 1000);

function mulberry32(seed: number): () => number {
    let a = seed;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function smooth(xs: number[], w: number): number[] {
    return xs.map((_, i) => {
        let s = 0;
        let c = 0;
        for (let k = Math.max(0, i - w); k <= Math.min(xs.length - 1, i + w); k++) {
            s += xs[k];
            c++;
        }
        return s / c;
    });
}

/** Ein Cut-Zeitpunkt je Kalendertag zwischen Start und END. */
function cutSeries(start: [number, number, number]): number[] {
    const out: number[] = [];
    const first = Date.UTC(start[0], start[1] - 1, start[2]);
    const last = Date.UTC(END[0], END[1] - 1, END[2]);
    for (let ms = first; ms <= last; ms += DAY * 1000) {
        const d = new Date(ms);
        out.push(utcTs(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()));
    }
    return out;
}

function effectiveTsOf(spec: Spec, ymd: [number, number, number], decisionTs: number): number {
    if (spec.effectiveLagDays === 0) return decisionTs;
    return utcTs(ymd[0], ymd[1], ymd[2] + spec.effectiveLagDays);
}

function buildHistory(spec: Spec, meetings: Meeting[], effective: number[]): PiprHistory {
    const rnd = mulberry32(spec.seed);
    const ts = cutSeries(spec.start);
    const n = ts.length;
    const noise = smooth(
        Array.from({ length: n }, () => rnd() * 2 - 1),
        6,
    );

    const anchorBp: number[] = [];
    const pendingBp: number[] = [];
    const impliedBp: number[] = [];
    const p: number[][] = [];
    const meetingIdx: number[] = [];
    const flags: number[] = [];

    for (let i = 0; i < n; i++) {
        const now = ts[i];

        let anchor = spec.anchorStart;
        let pending = 0;
        meetings.forEach((m, k) => {
            if (m.decidedBp === null || m.decisionTs > now) return;
            if (effective[k] <= now) anchor += m.decidedBp;
            else pending += m.decidedBp;
        });

        let idx = meetings.findIndex((m) => m.decisionTs > now);
        if (idx < 0) idx = meetings.length - 1;
        const nxt = meetings[idx];
        const outcome = nxt.decidedBp ?? spec.assumedNext;

        const dte = Math.max(0, (nxt.decisionTs - now) / DAY);
        const conv = Math.max(0, 1 - dte / 75);
        const target = Math.max(0, DELTAS.indexOf(outcome));

        let pt = 0.4 + 0.56 * Math.pow(conv, 1.5) + noise[i] * 0.9 * (1 - 0.7 * conv * conv);
        pt = Math.max(0.08, Math.min(0.985, pt));

        const decay = 1.15 + 0.5 * noise[i];
        const w = DELTAS.map((_, j) => (j === target ? 0 : Math.exp(-Math.abs(j - target) * decay)));
        const wsum = w.reduce((a, b) => a + b, 0) || 1;
        const probs = DELTAS.map((_, j) => (j === target ? pt : ((1 - pt) * w[j]) / wsum));

        const permille = probs.map((q) => Math.round(q * 1000));
        permille[target] += 1000 - permille.reduce((a, b) => a + b, 0);

        const marginal = permille.reduce((acc, q, j) => acc + (q / 1000) * DELTAS[j], 0);

        anchorBp.push(anchor);
        pendingBp.push(pending);
        impliedBp.push(Math.round((anchor + pending + marginal) * 100) / 100);
        p.push(permille);
        meetingIdx.push(idx);
        flags.push(rnd() < 0.03 ? 1 : 0);
    }

    return { ts, n, deltas: DELTAS, anchorBp, pendingBp, impliedBp, p, meetingIdx, flags };
}

/**
 * Erwarteter Pfad über die gelisteten Meetings.
 *
 * Wichtig: das Front-Meeting ist derselbe Datenpunkt wie das Ende der Historie,
 * nicht eine zweite Rechnung. In `pipr_signal` ist das die Zeile
 * (letztes ts, kleinstes meeting_date) — sie ist zugleich der letzte Punkt der
 * Front-Serie und der erste Punkt der Terminstruktur. Werden beide getrennt
 * erzeugt, endet die durchgezogene PIPR-Linie auf einem anderen Wert als der
 * erste Punkt des gestrichelten Pfads, und der Chart widerspricht sich selbst.
 *
 * Die Basis des Front-Meetings ist der beschlossene Stand, also anchor plus
 * pending — bis zum nächsten Meeting ist der Schritt in jedem Fall wirksam.
 */
function buildForward(
    spec: Spec,
    meetings: Meeting[],
    effective: number[],
    history: PiprHistory,
): PiprForward {
    const last = history.n - 1;
    const asofTs = history.ts[last];
    const anchorBp = history.anchorBp[last];
    const pendingBp = history.pendingBp[last];

    let announcedTs: number | null = null;
    let effectiveTs: number | null = null;
    if (pendingBp !== 0) {
        const k = meetings.findIndex(
            (m, j) => m.decidedBp !== null && m.decisionTs <= asofTs && effective[j] > asofTs,
        );
        if (k >= 0) {
            announcedTs = meetings[k].decisionTs;
            effectiveTs = effective[k];
        }
    }

    const future = meetings.filter((m) => m.decisionTs > asofTs).slice(0, LATER.length + 1);

    let base = anchorBp + pendingBp;
    const out = future.map((m, k) => {
        const p = k === 0 ? history.p[last] : LATER[k - 1];
        const rateBp =
            k === 0
                ? history.impliedBp[last]
                : Math.round(
                    (base + p.reduce((acc, q, j) => acc + (q / 1000) * DELTAS[j], 0)) * 100,
                ) / 100;
        const entry = { decisionTs: m.decisionTs, p, baseBp: base, rateBp };
        base = rateBp;
        return entry;
    });

    return {
        asofTs,
        anchorBp,
        pendingBp,
        pendingAnnouncedTs: announcedTs,
        pendingEffectiveTs: effectiveTs,
        deltas: DELTAS,
        meetings: out,
    };
}

export function buildSampleBundle(bank: Bank): PiprBundle {
    const spec = SPECS[bank];

    const meetings: Meeting[] = spec.meetings.map(([ymd, decidedBp]) => ({
        decisionTs: utcTs(ymd[0], ymd[1], ymd[2], spec.hourUtc, spec.minuteUtc),
        decidedBp,
    }));
    const effective = spec.meetings.map(([ymd], i) =>
        effectiveTsOf(spec, ymd, meetings[i].decisionTs),
    );

    const history = buildHistory(spec, meetings, effective);
    const forward = buildForward(spec, meetings, effective, history);

    return {
        schemaVersion: 2,
        methodologyVersion: "pipr-0.0.1",
        bank,
        cutTime: "00:00",
        cutTz: "UTC",
        generatedTs: forward.asofTs + 240,
        meetings,
        history,
        forward,
    };
}